/**
 * WgScriptDialog (rc148) — script RouterOS 7 del peer WireGuard, listo para
 * pegar en la terminal del MikroTik, con botón copiar.
 */
import { toast } from "sonner";
import { Copy, Loader2, Terminal, TriangleAlert } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { ApiError } from "@/lib/api";

import { useWgScript } from "./wgApi";

type Props = {
  peerId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    // Fallback (http sin contexto seguro / permisos)
    const ta = document.createElement("textarea");
    ta.value = texto;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

export function WgScriptDialog({ peerId, open, onOpenChange }: Props) {
  const { data, isLoading, error } = useWgScript(open ? peerId : null);

  async function copiar() {
    if (!data?.script) return;
    if (await copiarTexto(data.script)) toast.success("Script copiado al portapapeles");
    else toast.error("No se pudo copiar; selecciónalo a mano");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Terminal size={15} />
          Script RouterOS — {data?.descripcion ?? "…"}
        </DialogTitle>
        <p className="text-xs text-muted-foreground">
          IP del túnel: <code className="font-mono">{data?.ip_tunel ?? "…"}</code>
          {" "}· Pégalo completo en la terminal del MikroTik (RouterOS 7+).
        </p>
      </DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2 rounded-md border border-warn-soft bg-warn-soft px-2.5 py-2 text-[11px] text-warn">
            <TriangleAlert size={13} className="mt-0.5 shrink-0" />
            <span>
              Contiene la llave privada de este router. No lo compartas por chat.
              Se puede volver a pegar: primero quita lo que tenga el comentario
              <code className="mx-1 font-mono">byteisp-wg</code>. OpenVPN no se toca.
            </span>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-md border border-danger-soft bg-danger-soft px-2.5 py-2 text-xs text-danger">
              {(error as unknown as ApiError).message ?? "No se pudo generar el script"}
            </div>
          ) : (
            <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/30 p-3 font-mono text-[10px] leading-relaxed">
              {data?.script ?? ""}
            </pre>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <p className="text-[10px] text-muted-foreground">
              Al final el script imprime el peer; en unos segundos debe aparecer
              <span className="font-mono"> last-handshake</span>.
            </p>
            <Button size="sm" onClick={copiar} disabled={!data?.script}>
              <Copy size={12} /> Copiar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
